import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { backendUrl } from "@/lib/backendUrl";

const UNAVAILABLE_MESSAGE = "Questa proposta non \u00e8 pi\u00f9 disponibile o \u00e8 gi\u00e0 stata utilizzata.";

const STATES = {
  idle_confirm_required: {
    eyebrow: "Proposta recupero slot",
    title: "Vuoi accettare questo nuovo slot?",
    message: "Conferma solo se vuoi prenotare lo slot proposto dall'attività.",
  },
  loading: {
    eyebrow: "Proposta recupero slot",
    title: "Conferma in corso",
    message: "Stiamo confermando lo slot.",
  },
  success_confirmed: {
    eyebrow: "Slot confermato",
    title: "Slot confermato",
    message: "La tua prenotazione è stata registrata correttamente.",
  },
  slot_not_available: {
    eyebrow: "Proposta non disponibile",
    title: "Proposta non più disponibile",
    message: "Questo link non è più valido, è scaduto o lo slot è già stato assegnato.",
  },
  expired_or_invalid: {
    eyebrow: "Proposta non disponibile",
    title: "Proposta non più disponibile",
    message: "Questo link non è più valido, è scaduto o lo slot è già stato assegnato.",
  },
  generic_error: {
    eyebrow: "Errore",
    title: "Qualcosa è andato storto",
    message: "Riprova più tardi o contatta l'attività.",
  },
};

function mapResponseToState(status, payload) {
  if (status === 404) {
    return "expired_or_invalid";
  }

  const message = typeof payload?.message === "string" ? payload.message : "";

  if (status === 200 && message === "Slot confirmed and scheduled.") {
    return "success_confirmed";
  }

  if (status === 200 && message === UNAVAILABLE_MESSAGE) {
    return "slot_not_available";
  }

  return "generic_error";
}

export function PublicRecoveryConfirmPage() {
  const { token } = useParams();
  const [state, setState] = useState(token ? "idle_confirm_required" : "generic_error");

  const content = useMemo(() => STATES[state] || STATES.generic_error, [state]);
  const isLoading = state === "loading";
  const isActionable = state === "idle_confirm_required" && Boolean(token);
  const isPositive = state === "success_confirmed";

  const confirmRecovery = async () => {
    if (!token || isLoading) {
      return;
    }

    setState("loading");
    try {
      const response = await fetch(
        `${backendUrl}/api/notifications/confirm/${encodeURIComponent(token)}`,
        { method: "POST" }
      );
      let payload = null;
      try {
        payload = await response.json();
      } catch (error) {
        payload = null;
      }

      setState(mapResponseToState(response.status, payload));
    } catch (error) {
      setState("generic_error");
    }
  };

  return (
    <main
      className="min-h-screen bg-slate-50 px-4 py-8 flex items-center justify-center"
      data-testid="public-recovery-confirm-page"
    >
      <section className="w-full max-w-md bg-white border border-slate-200 rounded-sm p-6 shadow-sm">
        <div className="mb-6">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">FullBook</p>
          <p
            className="mt-4 text-xs font-medium uppercase tracking-[0.18em] text-slate-500"
            data-testid="public-recovery-confirm-eyebrow"
          >
            {content.eyebrow}
          </p>
        </div>

        <div
          className={`mb-5 flex h-11 w-11 items-center justify-center rounded-full border ${
            isLoading || state === "idle_confirm_required"
              ? "border-slate-200 bg-slate-50 text-slate-500"
              : isPositive
                ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                : "border-amber-200 bg-amber-50 text-amber-700"
          }`}
          aria-hidden="true"
        >
          {isLoading ? "..." : isPositive ? "OK" : "!"}
        </div>

        <h1
          className="text-2xl font-semibold text-slate-900 section-title"
          data-testid="public-recovery-confirm-title"
        >
          {content.title}
        </h1>
        <p
          className="mt-3 text-sm leading-6 text-slate-600"
          data-testid="public-recovery-confirm-message"
        >
          {content.message}
        </p>

        {isActionable && (
          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={confirmRecovery}
              className="h-11 flex-1 rounded-sm bg-slate-900 px-4 text-sm font-medium text-white transition-transform active:scale-95"
              data-testid="public-recovery-confirm-submit"
            >
              Accetta proposta
            </button>
            <button
              type="button"
              onClick={() => window.close()}
              className="h-11 flex-1 rounded-sm border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700"
              data-testid="public-recovery-confirm-close"
            >
              Chiudi pagina
            </button>
          </div>
        )}
      </section>
    </main>
  );
}
